import * as ko from 'knockout';
import page from 'page';

import { BaseForm } from './base_form';
import { User, ftUserOptions } from '../models/user';
import * as usersApi from '../api/users';
import * as groupsApi from '../api/groups';
import { FormSelectSearchConfiguration } from '../components/form_select_search';
import i18n from '../i18n';
import { Deferred } from '../utils/deferred';
import { createWithComponent } from '../utils/ko_utils';
import { session } from '../session';
import { confirmGroupSelection } from '../utils/plot_count_utils';
import {
  canChangeUsers,
  canChooseUnitGroup,
  canInviteUsers,
  customPermissionsEnabled,
} from '../permissions';
import { makeUnitGroupFieldProps } from '../utils/unit_group_field';

let template = require('raw-loader!../../templates/user_edit.html').default;

class UserEditScreen extends BaseForm<usersApi.UserData> {
  isAdmin = session.isAdmin;
  // Without a caption the select would display its first option while the value is still unset,
  // which reads as a filled field failing a required check.
  rolePlaceholder = i18n.t('Select a role')();
  // Editing an existing account and inviting a new one are separate permissions.
  canEdit = ko.observable(false);

  private isEditingOwnUser = ko.observable(false);

  user = ko.validatedObservable<User>(null);
  showUnitGroup = canChooseUnitGroup();
  unitGroupFieldProps = ko.pureComputed(() =>
    makeUnitGroupFieldProps(this.user().unitGroup, { disabled: !this.canEdit() })
  );
  isEditingOwnUserEmail = ko.pureComputed(
    () => this.isEditingOwnUser() && this.user()?.email() !== session.getEmail()
  );
  globalError = ko.observable('');
  languageList = SERVER_INFO.LANGUAGE_LIST;
  allowPasswordLogin = session.tenant()?.allow_password_login;

  groupSearchConfig = ko.pureComputed(() => {
    let config: FormSelectSearchConfiguration<groupsApi.BaseGroupData> = {
      title: i18n.t(['groups_colons', 'Groups:'])(),
      getSummaryName: (group) => {
        return group.name;
      },

      list: (params) => {
        return groupsApi.list(params && { include_trial_plot_counts: true });
      },

      entities: this.user() ? this.user().groups : undefined,

      create: { title: 'Group', componentName: 'group-edit' },
    };

    return config;
  });

  private subscriptions: KnockoutSubscription[] = [];

  constructor(
    params: {
      id: string;
      initialName?: string;
      result?: Deferred<usersApi.UserData>;
    },
    componentInfo: KnockoutComponentTypes.ComponentInfo
  ) {
    super(params);

    this.canEdit(params.id ? canChangeUsers() : canInviteUsers());

    if (this.user() && this.user().groups) {
      this.subscriptions.push(this.user().groups.subscribe(this.onGroupsChanged, null, 'arrayChange'));
    }

    const showPermissionRoles = customPermissionsEnabled();

    let userDataPromise = params.id ? usersApi.retrieve(params.id) : undefined;
    // Only the roles this user may hand out: the roles resource itself is admin-only, and offering
    // more than the allowlist would just produce a rejected save.
    let rolesPromise = showPermissionRoles ? usersApi.assignableRoles() : undefined;
    let promise = Promise.all([userDataPromise, rolesPromise]).then(([userData, roles]) => {
      this.user(new User(ftUserOptions, userData));

      if (!userData && params.initialName) {
        this.user().name(params.initialName);
      }

      if (roles) {
        // A new user starts with no role: it is a required, explicit choice rather than a default
        // picked by name or by list order.
        this.user().permissionRoleOptions(roles);
      }

      this.isEditingOwnUser(this.user().email() === session.getEmail());

      // Set hasAiAccess to false when the effective role is data_entry.
      if (this.user()) {
        this.subscriptions.push(
          this.user().effectiveRole.subscribe((newRole: usersApi.UserRole) => {
            if (newRole === 'data_entry') {
              this.user().hasAiAccess(false);
            }
          })
        );
      }
    });
    this.loadedAfter(promise).then(() => this.focusFirst(componentInfo.element));
  }

  save = () => {
    this.globalError('');

    if (this.validateLocal(this.user)) {
      let data = { ...this.user().toData() };
      const shouldForceLogout = this.isEditingOwnUserEmail();
      const onClose = shouldForceLogout ? () => page.redirect('/logout/') : undefined;

      this.executeSaveRequest(usersApi.save(data)).then((validation) => {
        this.onRemoteValidation(data, this.user(), validation, onClose);

        if (!validation.isValid && validation.errors['__all__']) {
          this.globalError(validation.errors['__all__'].join('. '));
        }
      });
    }
  };

  private onGroupsChanged = (changes: ko.utils.ArrayChange<groupsApi.BaseGroupData>[]) => {
    const onCancelGroupSelection = () => this.user().groups.pop();
    changes.forEach(function (change) {
      if (change.status === 'added') {
        confirmGroupSelection(change.value, onCancelGroupSelection);
      }
    });
  };

  dispose() {
    for (let subscription of this.subscriptions) {
      subscription.dispose();
    }

    this.subscriptions = [];
  }
}

export let userEdit = {
  name: 'user-edit',
  viewModel: createWithComponent(UserEditScreen),
  template: template,
};

ko.components.register(userEdit.name, userEdit);
